import { Role, Sector, getStoredToken, clearAuthData, isTokenExpired } from './auth';
import { parseErrorResponse } from './error';

/**
 * Serviços de administração
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://162.240.235.102:7006';

const jsonHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/**
 * Redireciona para login quando a sessão expira
 */
const handleSessionExpired = () => {
  clearAuthData();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const authHeaders = () => {
  const token = getStoredToken();
  if (!token) {
    handleSessionExpired();
    throw new Error('Usuário não autenticado');
  }
  // Verificar se o token está expirado
  if (isTokenExpired()) {
    handleSessionExpired();
    throw new Error('Sessão expirada');
  }
  return jsonHeaders(token);
};

// ==================== INTERFACES ====================

export interface AdminUser {
  id: string;
  email: string;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  roles: Role[];
  sector: Sector;
}

export interface RolesResponse {
  roles: Role[];
  description: {
    admin: string;
    staff: string;
    user: string;
  };
}

export interface SectorsResponse {
  sectors: Sector[];
}

export interface SeedAdminResponse {
  message: string;
  userId: string;
  email: string;
  role: Role;
}

export interface ChangeRoleResponse {
  message: string;
  userId: string;
  newRole: Role;
}

export interface ChangeSectorResponse {
  message: string;
  userId: string;
  newSector: Sector;
}

export interface PromoteResponse {
  message: string;
  userId: string;
}

export interface DeleteUserResponse {
  message: string;
}

// ==================== ADMIN FUNCTIONS ====================

/**
 * Criar primeiro administrador do sistema (só funciona uma vez)
 * POST /api/admin/seed-admin
 */
export const seedAdmin = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<SeedAdminResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/seed-admin`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, password }),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Listar todos os usuários
 * GET /api/admin/users
 */
export const getUsers = async (): Promise<AdminUser[]> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
    method: 'GET',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback = res.status === 403 ? 'Acesso negado' : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Obter detalhes de um usuário
 * GET /api/admin/users/{userId}
 */
export const getUser = async (userId: string): Promise<AdminUser> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback =
      res.status === 403
        ? 'Acesso negado'
        : res.status === 404
        ? 'Usuário não encontrado'
        : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Alterar role de um usuário
 * PUT /api/admin/users/{userId}/role
 */
export const changeUserRole = async (
  userId: string,
  role: Role
): Promise<ChangeRoleResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ role }),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback =
      res.status === 403
        ? 'Acesso negado'
        : res.status === 400
        ? 'Role inválida'
        : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Alterar setor de um usuário
 * PUT /api/admin/users/{userId}/sector
 */
export const changeUserSector = async (
  userId: string,
  sector: Sector
): Promise<ChangeSectorResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/sector`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ sector }),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback =
      res.status === 403
        ? 'Acesso negado'
        : res.status === 400
        ? 'Setor inválido'
        : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Promover usuário para Admin
 * POST /api/admin/users/{userId}/promote-admin
 */
export const promoteToAdmin = async (userId: string): Promise<PromoteResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/promote-admin`, {
    method: 'POST',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback = res.status === 403 ? 'Acesso negado' : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Promover usuário para Staff
 * POST /api/admin/users/{userId}/promote-staff
 */
export const promoteToStaff = async (userId: string): Promise<PromoteResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/promote-staff`, {
    method: 'POST',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback = res.status === 403 ? 'Acesso negado' : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Remover usuário
 * DELETE /api/admin/users/{userId}
 */
export const deleteUser = async (userId: string): Promise<DeleteUserResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback =
      res.status === 403
        ? 'Acesso negado'
        : res.status === 404
        ? 'Usuário não encontrado'
        : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Listar roles disponíveis
 * GET /api/admin/roles
 */
export const getRoles = async (): Promise<RolesResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/roles`, {
    method: 'GET',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback = res.status === 403 ? 'Acesso negado' : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Listar setores disponíveis (admin)
 * GET /api/admin/sectors
 */
export const getAdminSectors = async (): Promise<SectorsResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/admin/sectors`, {
    method: 'GET',
    headers: authHeaders(),
  });
  
  if (!res.ok) {
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    const fallback = res.status === 403 ? 'Acesso negado' : undefined;
    const { message } = await parseErrorResponse(res, fallback);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

